import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  type SingleAssets,
  type MulitpleAssets,
  type Poll,
  transformHtmlPostToText,
} from '@perfectpost/perfect-post-common';
import {ConsoleLogger} from 'aws-amplify/utils';
import React, {useMemo, useState, useRef, useEffect, forwardRef, useImperativeHandle} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import APPRECIATION from '../../assets/svg/APPRECIATION.svg';
import EMPATHY from '../../assets/svg/EMPATHY.svg';
import LIKE from '../../assets/svg/LIKE.svg';
import {transformTextToHtml} from '../../utils/text-transform';
import {CarrouselPlayer} from '../carrouselplayer/CarrouselPlayer';
import {VideoJS} from '../VideoJS';
import {PreviewCommentItem, type PreviewComment} from './PreviewCommentItem';

import 'video.js/dist/video-js.css';

const findLastVisibleWordInTextNode = (textNode: Node, containerRect: DOMRect): DOMRect | null => {
  if (textNode.nodeType !== Node.TEXT_NODE) return null;

  const words = textNode.textContent?.split(/\s+/) || [];
  const range = document.createRange();
  let lastVisibleWordRect: DOMRect | null = null;

  for (let i = 0; i < words.length; i++) {
    const currentText = words.slice(0, i + 1).join(' '); // Recrée le texte jusqu'au mot actuel

    range.setStart(textNode, 0);
    range.setEnd(textNode, currentText.length);

    const rect = range.getBoundingClientRect();

    // Vérifie si le mot est visible dans le conteneur
    if (rect.bottom <= containerRect.top + 60 && rect.top >= containerRect.top) {
      lastVisibleWordRect = rect;
    } else {
      break; // Arrête la boucle dès qu'un mot dépasse les limites
    }
  }

  range.detach(); // Nettoie le range après utilisation
  return lastVisibleWordRect;
};

const logger = new ConsoleLogger('ActivityPreview');

type ActivityPreviewProps = {
  picture?: string;
  fullname?: string;
  headline?: string;
  content: string;
  sharing?: SingleAssets | MulitpleAssets | Poll;
  firstComment?: string;
  /** Tous les commentaires programmés, dans l'ordre de publication. Prime sur `firstComment`. */
  comments?: PreviewComment[];
  previewMode?: 'laptop' | 'smartphone';
  likeCount?: number;
  commentCount?: number;
  onVideo?: (aspect: '9/16' | '4/3' | 'inconnu') => void;
};
export interface ActivityPreviewHandle {
  breakWordsElement: HTMLSpanElement | null;
}

export const ActivityPreview = forwardRef<ActivityPreviewHandle, ActivityPreviewProps>(
  (
    {
      previewMode,
      picture,
      fullname,
      headline,
      content,
      sharing,
      firstComment,
      comments,
      likeCount,
      commentCount,
      onVideo,
    },
    ref,
  ) => {
    const {t} = useTranslation();
    const [showMoreAction, toggleShowMoreAction] = useState(true);
    const [video916, setVideo916] = useState(false);

    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const descriptionRef = useRef<HTMLDivElement | null>(null); // Référence pour le parent avec overflow hidden
    const breakWordsRef = useRef<HTMLSpanElement | null>(null); // Référence pour "break-words"
    useImperativeHandle(ref, () => ({
      breakWordsElement: breakWordsRef.current,
    }));
    const width = useMemo(() => (previewMode === 'laptop' ? '555px' : '440px'), [previewMode]);

    const previewContent = useMemo(() => {
      return transformTextToHtml(transformHtmlPostToText(content));
    }, [content]);

    // Les commentaires programmés dans l'ordre de publication ; à défaut, l'ancien premier
    // commentaire (partage public d'un brouillon, conflit de versions), à son délai historique.
    const previewComments = useMemo<PreviewComment[]>(
      () =>
        comments ??
        (firstComment !== undefined && firstComment !== ''
          ? [{key: 'first-comment', content: firstComment, timeLabel: '10 mn', fullname, picture, headline}]
          : []),
      [comments, firstComment, fullname, picture, headline],
    );

    useEffect(() => {
      if (!descriptionRef.current || !buttonRef.current || !breakWordsRef.current) return;

      const descriptionElement = descriptionRef.current;
      const buttonElement = buttonRef.current;
      const elements = Array.from(breakWordsRef.current.childNodes);
      const containerRect = descriptionElement.getBoundingClientRect();

      let lastValidLineBreakSpan: HTMLElement | null = null;
      let lineCount = 0;
      let previousElement: ChildNode | null = null;
      let consecutiveSpanBr = false;
      let partiallyVisibleTextNode = false; // Indique si un TEXT_NODE est partiellement visible
      let lastVisibleWordRect: DOMRect | null = null; // Stocke les coordonnées du dernier mot visible
      let breaked = false;
      for (const element of elements) {
        if (
          element.nodeType === Node.COMMENT_NODE &&
          (previousElement === null || previousElement instanceof HTMLSpanElement)
        ) {
          previousElement = element;
        } else if (element.nodeType === Node.TEXT_NODE && previousElement?.nodeType === Node.COMMENT_NODE) {
          // Vérifie si le texte est dans les limites des 60px
          const range = document.createRange();
          range.selectNodeContents(element);
          const rect = range.getBoundingClientRect();
          range.detach(); // Nettoie le range

          const lineHeight = parseFloat(window.getComputedStyle(descriptionElement).lineHeight);

          // Vérifie si le texte est partiellement visible
          if (rect.top < containerRect.top + 60 && rect.bottom > containerRect.top + 60) {
            partiallyVisibleTextNode = true;
            lastVisibleWordRect = findLastVisibleWordInTextNode(element, containerRect);
            breaked = true;
            break;
          }

          // Vérifie si le texte dépasse les limites
          if (rect.bottom > containerRect.top + 60) {
            breaked = true;
            break;
          }

          // Vérifie si le texte s'étend sur plus d'une ligne et est dans les limites visibles
          const linesCovered = Math.ceil((rect.bottom - rect.top) / lineHeight);
          if (linesCovered > 1) {
            lineCount += linesCovered;
          } else {
            lineCount++;
          }

          if (consecutiveSpanBr) {
            lineCount++;
            consecutiveSpanBr = false;
          }
          previousElement = element;
        } else if (element.nodeType === Node.COMMENT_NODE && previousElement?.nodeType === Node.TEXT_NODE) {
          previousElement = element;
        } else if (element instanceof HTMLSpanElement && previousElement?.nodeType === Node.COMMENT_NODE) {
          previousElement = element;
          // Vérifie si le span est dans les limites des 60px
          const rect = element.getBoundingClientRect();
          if (rect.bottom <= containerRect.top + 60) {
            lastValidLineBreakSpan = element;
          } else {
            breaked = true;
            break;
          }
        } else if (element instanceof HTMLSpanElement && previousElement instanceof HTMLSpanElement) {
          consecutiveSpanBr = true;
          // Vérifie si le span est dans les limites des 60px
          const rect = element.getBoundingClientRect();
          if (rect.bottom > containerRect.top + 60) {
            breaked = true;
            break;
          }
        }
        previousElement = element;
      }
      if (partiallyVisibleTextNode) {
        logger.debug('Forçage à 3 lignes en raison d’un TEXT_NODE partiellement visible');
        lineCount = 3;
        lastValidLineBreakSpan = null;
      }

      // Applique la classe en fonction du nombre de lignes
      descriptionElement.classList.remove(
        'feed-shared-inline-show-more-text--1-lines',
        'feed-shared-inline-show-more-text--2-lines',
        'feed-shared-inline-show-more-text--3-lines',
      );
      buttonElement.style.display = 'inline-block';

      if (lineCount === 1) {
        descriptionElement.classList.add('feed-shared-inline-show-more-text--1-lines');
      } else if (lineCount === 2) {
        descriptionElement.classList.add('feed-shared-inline-show-more-text--2-lines');
      } else {
        descriptionElement.classList.add('feed-shared-inline-show-more-text--3-lines');
      }

      logger.debug(`Nombre de lignes affichées : ${lineCount}`);

      if (breaked === false && lineCount <= 3) {
        buttonElement.style.display = 'none';
      } else if (partiallyVisibleTextNode) {
        const btnSpan = buttonElement.querySelector('span')?.getBoundingClientRect();
        if (previewMode === 'laptop') {
          // Positionne le bouton tout à droite du conteneur
          buttonElement.style.left = `${(lastVisibleWordRect?.right ?? 476) - containerRect.left - (btnSpan?.width ?? 42)}px`;
          buttonElement.style.right = '';
        } else {
          buttonElement.style.left = '';
          buttonElement.style.right = '0px';
        }
      } else if (lastValidLineBreakSpan) {
        const rect = lastValidLineBreakSpan.getBoundingClientRect();
        if (previewMode === 'laptop') {
          // Place le bouton dynamiquement à droite du dernier <span><br></span>
          buttonElement.style.left = `${rect.right - containerRect.left}px`;
          buttonElement.style.right = '';
        } else {
          buttonElement.style.left = '';
          buttonElement.style.right = '0px';
        }
      } else {
        buttonElement.style.left = '';
        buttonElement.style.right = '';
      }
    }, [previewContent, previewMode]);

    const onAspectRatioDetected = (aspect: '9/16' | '4/3' | 'inconnu') => {
      setVideo916(aspect === '9/16');
      onVideo?.(aspect);
    };

    return previewMode === 'smartphone' && sharing?.type === 'video' && video916 ? (
      <Box
        className="feed-shared-update-v2"
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          width,
          p: 1,
          fontFamily:
            '-apple-system, system-ui, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Fira Sans, Ubuntu, Oxygen, Oxygen Sans, Cantarell, Droid Sans, Apple Color Emoji, Segoe UI Emoji, Segoe UI Emoji, Segoe UI Symbol, Lucida Grande, Helvetica, Arial, sans-serif',
        })}
      >
        <Box sx={{width: '424px', borderRadius: '8px', overflow: 'hidden', position: 'relative'}}>
          <Box
            className="update-components-actor"
            sx={{position: 'absolute', zIndex: 1, display: 'flex', flexDirection: 'row', p: 1, gap: 1}}
          >
            <Box
              className="update-components-actor__image"
              sx={{
                marginTop: '2px',
                marginLeft: '2px',
                flexShrink: 0,
                display: 'flex',
                position: 'relative',
                width: 48,
                height: 48,
              }}
            >
              <Box
                component="img"
                sx={{
                  objectFix: 'contain',
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  objectPosition: 'center',
                  backgroundPosition: '50%',
                  borderRadius: '50%',
                }}
                src={picture}
                loading="lazy"
              />
            </Box>
            <Box sx={{display: 'flex', flexDirection: 'column', color: 'white', justifyContent: 'center'}}>
              <Box
                className="update-components-actor__meta"
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                }}
              >
                {fullname}
              </Box>
              <Box
                component="span"
                className="update-components-actor__description"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '16px',
                  color: (theme) => (theme.palette.mode === 'light' ? 'white' : 'rgb(255 255 255 / .6)'),
                  maxWidth: '350px',
                }}
              >
                <span>{headline}</span>
              </Box>
            </Box>
          </Box>
          <Box sx={{width: '100%'}}>
            <VideoJS
              options={{
                autoplay: false,
                sources: [{src: sharing.path}],
                html5: true,
                controls: true,
              }}
              src={sharing.path}
              playsInline
              preload="metadata"
              muted
              className="video-js media-player__player vjs-paused vjs-9-16 media-player--use-mercado vjs-controls-enabled vjs-workinghover vjs-v7 vjs-user-active vjs-layout-medium"
              onAspectRatioDetected={onAspectRatioDetected}
            />
          </Box>
          <Box sx={{position: 'absolute', bottom: 0, width: '100%', p: 1, height: '38px'}}>
            <Box
              component="span"
              sx={{
                color: 'white',
                display: '-webkit-box', // Nécessaire pour le clamping sur webkit
                WebkitLineClamp: 1, // Limite à une ligne
                WebkitBoxOrient: 'vertical', // Définit l'orientation de la boîte en colonne
                overflow: 'hidden', // Cache le contenu qui déborde
              }}
              dangerouslySetInnerHTML={{__html: previewContent}}
            />
            <Box
              sx={{
                position: 'absolute',
                right: '10px',
                color: 'white',
                fontSize: '12px',
                top: '5px',
                backgroundColor: 'black',
                borderRadius: '50%',
              }}
            >
              <VolumeOffIcon />
            </Box>
          </Box>
        </Box>
        <Box
          className="update-v2-social-activity"
          sx={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', mt: 1}}
        >
          <Box
            className="social-details-social-counts"
            sx={{
              borderBottom: '1px solid rgba(140, 140, 140, 0.2)',
              display: 'flex',
              padding: 0,
            }}
          >
            <Box sx={{flexGrow: 1, width: '100%'}}>
              <Box component="ul" sx={{flexWrap: 'wrap', display: 'flex', margin: 0, padding: 0}}>
                <Box
                  component="li"
                  sx={{
                    listStyle: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    marginRight: '4px',
                    minHeight: '32px',
                  }}
                >
                  <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <Box component="img" src={EMPATHY} alt="love" />
                    <Box component="img" src={LIKE} alt="like" sx={{margin: '0 0 0 -.4px'}} />
                    <Box component="img" src={APPRECIATION} alt="support" sx={{margin: '0 0 0 -4px'}} />
                    <Box
                      component="span"
                      sx={{
                        display: 'flex',
                        flexGrow: 1,
                        overflow: 'hidden',
                        flexWrap: 'wrap',
                        maxHeight: '18px',
                        marginRight: '24px',
                      }}
                    >
                      <Box
                        className="social-details-social-counts__social-proof-text"
                        component="span"
                        sx={{
                          float: 'left',
                          paddingLeft: '4px',
                          lineHeight: '17.5px',
                          fontSize: '14px',
                          fontWeight: 400,
                          color: (theme) =>
                            theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        {likeCount ?? 99}
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box component="li" sx={{display: 'flex', flexGrow: 1, maxWidth: '100%', alignItem: 'center'}}>
                  <Box
                    component="span"
                    sx={{
                      marginLeft: 'auto',
                      marginRight: 0,
                      lineHeight: '17.5px',
                      fontSize: '14px',
                      fontWeight: 400,
                      color: (theme) =>
                        theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                    }}
                  >
                    {t('activitypreview.socialProof.comment', {
                      count: commentCount ?? 99,
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            className="feed-shared-social-action-bar"
            sx={{
              display: 'grid',
              columnGap: '4px',
              justifyContent: 'initial',
              gridAutoFlow: 'column',
              gridTemplateColumns: '66px auto',
              mt: 1,
            }}
          >
            <Box className="feed-shared-social-action-bar__action-button">
              <Box
                component="button"
                sx={{
                  width: '100%',
                  padding: '8px 10px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box component="img" sx={{width: '24px', height: '24px'}} src={picture} loading="lazy"></Box>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  id="caret-small"
                  width="16"
                  height="16"
                  fill="currentColor"
                >
                  <path d="M8 11L3 6h10z" fillRule="evenodd"></path>
                </svg>
              </Box>
            </Box>
            <Box component="span" className="reactions-react-button">
              <Box
                component="button"
                sx={{
                  padding: '4px 16px',
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: 'transparent',
                  border: 'none',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    id="thumbs-up-outline-small"
                    width="16"
                    height="16"
                    fill="currentColor"
                  >
                    <path d="M12.91 7l-2.25-2.57a8.21 8.21 0 01-1.5-2.55L9 1.37A2.08 2.08 0 007 0a2.08 2.08 0 00-2.06 2.08v1.17a5.81 5.81 0 00.31 1.89l.28.86H2.38A1.47 1.47 0 001 7.47a1.45 1.45 0 00.64 1.21 1.48 1.48 0 00-.37 2.06 1.54 1.54 0 00.62.51h.05a1.6 1.6 0 00-.19.71A1.47 1.47 0 003 13.42v.1A1.46 1.46 0 004.4 15h4.83a5.61 5.61 0 002.48-.58l1-.42H14V7zM12 12.11l-1.19.52a3.59 3.59 0 01-1.58.37H5.1a.55.55 0 01-.53-.4l-.14-.48-.49-.21a.56.56 0 01-.34-.6l.09-.56-.42-.42a.56.56 0 01-.09-.68L3.55 9l-.4-.61A.28.28 0 013.3 8h5L7.14 4.51a4.15 4.15 0 01-.2-1.26V2.08A.09.09 0 017 2a.11.11 0 01.08 0l.18.51a10 10 0 001.9 3.24l2.84 3z"></path>
                  </svg>
                  <span>{t('activitypreview.like')}</span>
                </Box>
              </Box>
            </Box>
            <Box component="span" className="reactions-react-button">
              <Box
                component="button"
                sx={{
                  padding: '4px 16px',
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: 'transparent',
                  border: 'none',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    id="comment-small"
                    width="16"
                    height="16"
                    fill="currentColor"
                  >
                    <path d="M5 8h5v1H5zm11-.5v.08a6 6 0 01-2.75 5L8 16v-3H5.5A5.51 5.51 0 010 7.5 5.62 5.62 0 015.74 2h4.76A5.5 5.5 0 0116 7.5zm-2 0A3.5 3.5 0 0010.5 4H5.74A3.62 3.62 0 002 7.5 3.53 3.53 0 005.5 11H10v1.33l2.17-1.39A4 4 0 0014 7.58zM5 7h6V6H5z"></path>
                  </svg>
                  <span>{t('activitypreview.comment')}</span>
                </Box>
              </Box>
            </Box>
            <Box component="span" className="reactions-react-button">
              <Box
                component="button"
                sx={{
                  padding: '4px 16px',
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: 'transparent',
                  border: 'none',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    id="repost-small"
                    width="16"
                    height="16"
                    fill="currentColor"
                  >
                    <path d="M4 10H2V5c0-1.66 1.34-3 3-3h3.85L7.42 0h2.44L12 3 9.86 6H7.42l1.43-2H5c-.55 0-1 .45-1 1v5zm8-4v5c0 .55-.45 1-1 1H7.15l1.43-2H6.14L4 13l2.14 3h2.44l-1.43-2H11c1.66 0 3-1.34 3-3V6h-2z"></path>
                  </svg>
                  <span>{t('activitypreview.repost')}</span>
                </Box>
              </Box>
            </Box>
            <Box component="span" className="reactions-react-button">
              <Box
                component="button"
                sx={{
                  padding: '4px 16px',
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: 'transparent',
                  border: 'none',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    id="send-privately-small"
                    width="16"
                    height="16"
                    data-supported-dps="16x16"
                    fill="currentColor"
                  >
                    <path d="M14 2L0 6.67l5 2.64 5.67-3.98L6.7 11l2.63 5L14 2z"></path>
                  </svg>
                  <span>{t('activitypreview.send')}</span>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    ) : (
      <Box
        className="feed-shared-update-v2"
        sx={(theme) => ({
          borderRadius: previewMode === 'smartphone' ? 0 : '8px',
          backgroundColor: theme.palette.background.paper,
          width,
          fontFamily:
            '-apple-system, system-ui, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Fira Sans, Ubuntu, Oxygen, Oxygen Sans, Cantarell, Droid Sans, Apple Color Emoji, Segoe UI Emoji, Segoe UI Emoji, Segoe UI Symbol, Lucida Grande, Helvetica, Arial, sans-serif',
        })}
      >
        <Box className="relative" sx={{position: 'relative'}}>
          <Box
            className="update-components-actor--with-control-menu-and-hide-post"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'nowrap',
              padding: '12px 72px 0 16px',
              marginBottom: '8px',
              alignItem: 'center',
            }}
          >
            <Box
              className="update-components-actor__container"
              sx={{paddingRight: '16px', display: 'flex', flexGrow: 1, overflow: 'hidden'}}
            >
              <Box
                className="update-components-actor__image"
                sx={{
                  marginTop: '2px',
                  marginLeft: '2px',
                  flexShrink: 0,
                  display: 'flex',
                  position: 'relative',
                  width: 48,
                  height: 48,
                }}
              >
                <Box
                  component="img"
                  sx={{
                    objectFix: 'contain',
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    objectPosition: 'center',
                    backgroundPosition: '50%',
                    borderRadius: '50%',
                  }}
                  src={picture}
                  loading="lazy"
                />
              </Box>
              <Box
                className="update-components-actor__meta"
                sx={{
                  padding: '2px',
                  flexGrow: 1,
                  flexBasis: 0,
                  marginLeft: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box className="update-components-actor__meta-link">
                  <Box
                    component="span"
                    className="update-components-actor__title"
                    sx={{
                      display: 'flex',
                    }}
                  >
                    <Box
                      component="span"
                      className="update-components-actor__single-line-truncate"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '14px',
                        fontWeight: 600,
                        lineHeight: '20px',
                      }}
                    >
                      {fullname}
                    </Box>
                    <Box
                      component="span"
                      className="update-components-actor__supplementary-actor-info"
                      sx={{
                        flexShrink: 0,
                        verticalAlign: 'top',
                        whiteSpace: 'nowrap',
                        color: (theme) =>
                          theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      <span style={{whiteSpace: 'pre'}}> </span>• {t('activitypreview.you')}
                    </Box>
                  </Box>
                  <Box
                    component="span"
                    className="update-components-actor__description"
                    sx={{
                      paddingRight: previewMode === 'laptop' ? '72px' : undefined,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: '16px',
                      color: (theme) =>
                        theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                    }}
                  >
                    <span>{headline}</span>
                  </Box>
                  <Box className="update-components-actor__sub-description-link">
                    <Box
                      component="span"
                      className="update-components-actor__sub-description"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: '16px',
                        color: (theme) =>
                          theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                      }}
                    >
                      <span>{t('activitypreview.oneday')}</span>
                      <span> • </span>
                      <span>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            overflow: 'hidden',
                            position: 'relative',
                            verticalAlign: 'bottom',
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 16 16"
                            data-supported-dps="16x16"
                            fill="currentColor"
                            className="mercado-match"
                            width="16"
                            height="16"
                            focusable="false"
                          >
                            <path d="M8 1a7 7 0 107 7 7 7 0 00-7-7zM3 8a5 5 0 011-3l.55.55A1.5 1.5 0 015 6.62v1.07a.75.75 0 00.22.53l.56.56a.75.75 0 00.53.22H7v.69a.75.75 0 00.22.53l.56.56a.75.75 0 01.22.53V13a5 5 0 01-5-5zm6.24 4.83l2-2.46a.75.75 0 00.09-.8l-.58-1.16A.76.76 0 0010 8H7v-.19a.51.51 0 01.28-.45l.38-.19a.74.74 0 01.68 0L9 7.5l.38-.7a1 1 0 00.12-.48v-.85a.78.78 0 01.21-.53l1.07-1.09a5 5 0 01-1.54 9z"></path>
                          </svg>
                        </Box>
                      </span>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            className="feed-shared-update-v2__control-menu"
            sx={{position: 'absolute', top: '4px', right: '8px', display: 'flex'}}
          >
            <Box sx={{width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <MoreHorizIcon sx={{width: '16px', height: '16px'}} />
            </Box>
            <Box
              sx={{
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '4px',
              }}
            >
              <CloseIcon sx={{width: '16px', height: '16px'}} />
            </Box>
          </Box>
        </Box>
        <Box className="feed-shared-update-v2__description-wrapper" sx={{width}}>
          <Box
            className={`feed-shared-update-v2__description feed-shared-inline-show-more-text--3-lines ${showMoreAction ? 'feed-shared-inline-show-more-text' : ''}`}
            ref={descriptionRef}
            sx={{
              position: 'relative',
              display: 'block',
              margin: '0 16px',
              lineHeight: '20px',
              padding: 0,
              '&.feed-shared-inline-show-more-text': {
                maxWidth: '928px',
                overflow: 'hidden',
              },
              '&.feed-shared-inline-show-more-text.feed-shared-inline-show-more-text--1-lines': {
                maxHeight: '20px',
              },
              '&.feed-shared-inline-show-more-text.feed-shared-inline-show-more-text--2-lines': {
                maxHeight: '40px',
              },
              '&.feed-shared-inline-show-more-text.feed-shared-inline-show-more-text--3-lines': {
                maxHeight: '60px',
              },
            }}
          >
            <Box
              className="update-components-text"
              sx={{
                lineHeight: 'inherit',
                fontSize: '14px',
                position: 'relative',
                margin: 0,
                padding: 0,
                verticalAlign: 'baseline',
              }}
            >
              <Box
                component="span"
                className="break-words"
                ref={breakWordsRef}
                sx={{
                  color: 'inherit',
                  lineHeight: 'inherit',
                  overflowWrap: 'break-word',
                  wordWrap: 'break-word',
                  wordBreak: 'break-word',
                  fontFamily:
                    '-apple-system, system-ui, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Fira Sans, Ubuntu, Oxygen, Oxygen Sans, Cantarell, Droid Sans, Apple Color Emoji, Segoe UI Emoji, Segoe UI Emoji, Segoe UI Symbol, Lucida Grande, Helvetica, Arial, sans-serif',
                  '& .mention-link, & .hashtag-link, & .link': {
                    display: 'inline-block',
                    position: 'relative',
                    wordBreak: 'normal',
                    overflowWrap: 'normal',
                    color: '#0a66c2',
                    textDecoration: 'none',
                    fontWeight: 600,
                  },
                }}
                dangerouslySetInnerHTML={{__html: previewContent}}
              ></Box>
            </Box>
            {showMoreAction && (
              <Box
                component="button"
                role="button"
                ref={buttonRef}
                sx={(theme) => ({
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  backgroundColor: theme.palette.background.paper,
                  lineHeight: 'inherit',
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  marginBottom: 0,
                  border: 'none',
                  padding: 0,
                  paddingLeft: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: (theme) =>
                    theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                  fontFamily:
                    '-apple-system, system-ui, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Fira Sans, Ubuntu, Oxygen, Oxygen Sans, Cantarell, Droid Sans, Apple Color Emoji, Segoe UI Emoji, Segoe UI Emoji, Segoe UI Symbol, Lucida Grande, Helvetica, Arial, sans-serif',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#0a66c2',
                  },
                })}
                onClick={() => toggleShowMoreAction((b) => !b)}
                type="button"
              >
                <Box component="span" sx={{display: 'inline-block'}}>
                  {t('activitypreview.seeMore.more')}
                </Box>
              </Box>
            )}
          </Box>
          {showMoreAction === false && (
            <Box sx={{marginTop: '8px', width: '100%', display: 'flex', justifyContent: 'flex-end'}}>
              <Box
                component="button"
                role="button"
                sx={{
                  margin: '0 32px 16px 0',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  lineHeight: 'inherit',
                  border: 'none',
                  padding: 0,
                  paddingLeft: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: (theme) =>
                    theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                  backgroundColor: 'transparent',
                  fontFamily:
                    '-apple-system, system-ui, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Fira Sans, Ubuntu, Oxygen, Oxygen Sans, Cantarell, Droid Sans, Apple Color Emoji, Segoe UI Emoji, Segoe UI Emoji, Segoe UI Symbol, Lucida Grande, Helvetica, Arial, sans-serif',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#0a66c2',
                  },
                }}
                onClick={() => toggleShowMoreAction((b) => !b)}
                type="button"
              >
                <Box component="span" sx={{display: 'inline-block'}}>
                  {t('activitypreview.seeMore.less')}
                </Box>
              </Box>
            </Box>
          )}
          <Box sx={{marginTop: '8px', width: '100%'}}>
            {sharing?.type === 'image' && <Box component="img" src={sharing.path} sx={{width: '100%'}} />}
            {sharing?.type === 'pdf' && sharing.path && sharing.path.trim().length > 0 && (
              <Box sx={{width: 540}}>
                <CarrouselPlayer url={sharing.path} />
              </Box>
            )}
            {sharing?.type === 'video' && sharing.path !== '' && (
              <Box sx={{width: '100%'}} data-vjs-player>
                <VideoJS
                  options={{
                    autoplay: false,
                    sources: [{src: sharing.path}],
                    html5: true,
                    controls: true,
                  }}
                  src={sharing.path}
                  playsInline
                  preload="metadata"
                  muted
                  className="video-js media-player__player vjs-paused vjs-fluid vjs-1-1 media-player--use-mercado vjs-controls-enabled vjs-workinghover vjs-v7 vjs-user-active vjs-layout-medium"
                  onAspectRatioDetected={onAspectRatioDetected}
                />
              </Box>
            )}
            {sharing?.type === 'poll' && (
              <Box
                sx={{
                  padding: '16px',
                  margin: '8px 16px 0 16px',
                  border: '0 none rgba(0,0,0, 0.9)',
                  boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px',
                  borderRadius: '8px',
                }}
              >
                <Box sx={{marginBottom: '16px'}}>
                  <Typography sx={{fontSize: '16px', fontWeight: 600}}>{sharing.question}</Typography>
                </Box>
                <Box component="fieldset" sx={{border: 'none'}}>
                  <Box
                    sx={{
                      borderRadius: '16px',
                      border: '16px none rgb(10, 102, 194)',
                      boxShadow: 'rgb(10, 102, 194) 0 0 0 1px inset',
                      marginBottom: '8px',
                      textAlign: 'center',
                      fontWeight: 600,
                      fontSize: '16px',
                      color: 'rgb(10,102,194)',
                    }}
                  >
                    {sharing.option1}
                  </Box>
                  <Box
                    sx={{
                      borderRadius: '16px',
                      border: '16px none rgb(10, 102, 194)',
                      boxShadow: 'rgb(10, 102, 194) 0 0 0 1px inset',
                      marginBottom: '8px',
                      textAlign: 'center',
                      fontWeight: 600,
                      fontSize: '16px',
                      color: 'rgb(10,102,194)',
                    }}
                  >
                    {sharing.option2}
                  </Box>
                  {sharing.option3 && (
                    <Box
                      sx={{
                        borderRadius: '16px',
                        border: '16px none rgb(10, 102, 194)',
                        boxShadow: 'rgb(10, 102, 194) 0 0 0 1px inset',
                        marginBottom: '8px',
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: '16px',
                        color: 'rgb(10,102,194)',
                      }}
                    >
                      {sharing.option3}
                    </Box>
                  )}
                  {sharing.option4 && (
                    <Box
                      sx={{
                        borderRadius: '16px',
                        border: '16px none rgb(10, 102, 194)',
                        boxShadow: 'rgb(10, 102, 194) 0 0 0 1px inset',
                        marginBottom: '8px',
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: '16px',
                        color: 'rgb(10,102,194)',
                      }}
                    >
                      {sharing.option4}
                    </Box>
                  )}
                </Box>
                <Typography
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                    fontSize: '14px',
                    paddingTop: '8px',
                  }}
                >
                  {t('activitypreview.poll.status', {count: sharing.duration})}
                </Typography>
              </Box>
            )}
            {sharing?.type === 'images' && sharing.images.length > 0 && (
              <Box
                className="update-components-image--smart-grid"
                sx={{
                  position: 'relative',
                  boxSizing: 'border-box',
                  display: 'grid',
                  gridTemplateColumns: sharing.images.length === 2 ? 'auto auto' : 'auto minmax(33.33%, 1fr)',
                  gridTemplateRows:
                    sharing.images.length === 2
                      ? '100%'
                      : `repeat(${sharing.images.length === 3 ? '2' : '3'}, ${sharing.images.length === 3 ? '50%' : '33.33%'})`,
                  maxHeight: `calc(${width} - 3px)`,
                  minHeight: 'auto',
                  height: 'auto',
                  margin: 0,
                  padding: 0,
                  overflow: 'clip',
                  width: '100%',
                  aspectRatio: '1.2 / 1',
                }}
              >
                {sharing.images.slice(0, 4).map((image, index) => (
                  <Box
                    key={index}
                    component="button"
                    className="update-components-image__image-link"
                    sx={[
                      {
                        position: 'relative',
                        boxSizing: 'border-box',
                        padding: 0,
                        border: 'none',
                        height: '100%',
                        width: '100%',
                        left: 0,
                        top: 0,
                        margin: 0,
                        display: 'block',
                      },
                      index === 0 && sharing.images.length >= 3
                        ? {
                            maxHeight: `calc(${width} - 3px)`,
                            gridColumn: 1,
                            gridRow: '1 / 4',
                            aspectRatio: '0.8 / 1',
                          }
                        : {},
                    ]}
                    aria-label={`Image ${index + 1}`}
                  >
                    <Box
                      sx={{
                        display: 'block',
                        boxSizing: 'border-box',
                        height: '100%',
                        margin: 0,
                        padding: 0,
                        width: '100%',
                      }}
                    >
                      <img
                        src={image.path}
                        alt={image.title || `Image ${index + 1}`}
                        loading="lazy"
                        width="600"
                        height="750"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          maxHeight: `calc(${width} - 3px)`,
                          border: '1px solid transparent',
                          display: 'block',
                          margin: 0,
                          padding: 0,
                          overflow: 'clip',
                          aspectRatio: 'auto 600 / 750',
                        }}
                      />
                    </Box>
                    {index === 3 && sharing.images.length > 4 && (
                      <Box
                        className="update-components-image__excess-image-count-overlay"
                        sx={{
                          position: 'absolute',
                          top: '1px',
                          left: '1px',
                          width: 'calc(100% - 2px)',
                          height: 'calc(100% - 2px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontSize: '24px',
                          fontWeight: 'bold',
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        }}
                      >
                        +{sharing.images.length - 4}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
        <Box
          className="update-v2-social-activity"
          sx={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-end'}}
        >
          <Box
            className="social-details-social-counts"
            sx={{
              borderBottom: '1px solid rgba(140, 140, 140, 0.2)',
              display: 'flex',
              padding: 0,
              margin: '0 16px',
            }}
          >
            <Box sx={{flexGrow: 1, width: '100%'}}>
              <Box component="ul" sx={{flexWrap: 'wrap', display: 'flex', margin: 0, padding: 0}}>
                <Box
                  component="li"
                  sx={{
                    listStyle: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    marginRight: '4px',
                    minHeight: '32px',
                  }}
                >
                  <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <Box component="img" src={EMPATHY} alt="love" />
                    <Box component="img" src={LIKE} alt="like" sx={{margin: '0 0 0 -.4px'}} />
                    <Box component="img" src={APPRECIATION} alt="support" sx={{margin: '0 0 0 -4px'}} />
                    <Box
                      component="span"
                      sx={{
                        display: 'flex',
                        flexGrow: 1,
                        overflow: 'hidden',
                        flexWrap: 'wrap',
                        maxHeight: '18px',
                        marginRight: '24px',
                      }}
                    >
                      <Box
                        className="social-details-social-counts__social-proof-text"
                        component="span"
                        sx={{
                          float: 'left',
                          paddingLeft: '4px',
                          lineHeight: '17.5px',
                          fontSize: '14px',
                          fontWeight: 400,
                          color: (theme) =>
                            theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        {likeCount ??
                          (previewMode === 'smartphone' ? (
                            99
                          ) : (
                            // Le clin d'œil assumé du fondateur : son nom, et le lien vers son profil.
                            <Trans
                              i18nKey="activitypreview.socialProof.like"
                              components={{
                                profil: (
                                  <Box
                                    component="a"
                                    href="https://www.linkedin.com/in/jeromeheissler"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      color: 'inherit',
                                      textDecoration: 'none',
                                      '&:hover': {textDecoration: 'underline'},
                                    }}
                                  />
                                ),
                              }}
                            />
                          ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box component="li" sx={{display: 'flex', flexGrow: 1, maxWidth: '100%'}}>
                  <Box
                    component="span"
                    sx={{
                      marginLeft: 'auto',
                      marginRight: 0,
                      lineHeight: '17.5px',
                      fontSize: '14px',
                      fontWeight: 400,
                      color: (theme) =>
                        theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                    }}
                  >
                    {t('activitypreview.socialProof.comment', {
                      count: commentCount ?? 99,
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            className="feed-shared-social-action-bar"
            sx={{
              display: 'grid',
              columnGap: '4px',
              justifyContent: 'initial',
              gridAutoFlow: 'column',
              gridTemplateColumns: '66px auto',
              padding: '4px 16px',
              '& .reactions-react-button button': {
                color: (theme) =>
                  theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.75)',
              },
            }}
          >
            <Box className="feed-shared-social-action-bar__action-button">
              <Box
                component="button"
                sx={{
                  width: '100%',
                  padding: '8px 10px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  color: (theme) =>
                    theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.75)',
                }}
              >
                <Box
                  component="img"
                  sx={{width: '24px', height: '24px', borderRadius: '50%'}}
                  src={picture}
                  loading="lazy"
                ></Box>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  id="caret-small"
                  width="16"
                  height="16"
                  fill="currentColor"
                >
                  <path d="M8 11L3 6h10z" fillRule="evenodd"></path>
                </svg>
              </Box>
            </Box>
            <Box component="span" className="reactions-react-button">
              <Box
                component="button"
                sx={{
                  padding: '4px 16px',
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: 'transparent',
                  border: 'none',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    id="thumbs-up-outline-small"
                    width="16"
                    height="16"
                    fill="currentColor"
                  >
                    <path d="M12.91 7l-2.25-2.57a8.21 8.21 0 01-1.5-2.55L9 1.37A2.08 2.08 0 007 0a2.08 2.08 0 00-2.06 2.08v1.17a5.81 5.81 0 00.31 1.89l.28.86H2.38A1.47 1.47 0 001 7.47a1.45 1.45 0 00.64 1.21 1.48 1.48 0 00-.37 2.06 1.54 1.54 0 00.62.51h.05a1.6 1.6 0 00-.19.71A1.47 1.47 0 003 13.42v.1A1.46 1.46 0 004.4 15h4.83a5.61 5.61 0 002.48-.58l1-.42H14V7zM12 12.11l-1.19.52a3.59 3.59 0 01-1.58.37H5.1a.55.55 0 01-.53-.4l-.14-.48-.49-.21a.56.56 0 01-.34-.6l.09-.56-.42-.42a.56.56 0 01-.09-.68L3.55 9l-.4-.61A.28.28 0 013.3 8h5L7.14 4.51a4.15 4.15 0 01-.2-1.26V2.08A.09.09 0 017 2a.11.11 0 01.08 0l.18.51a10 10 0 001.9 3.24l2.84 3z"></path>
                  </svg>
                  <span>{t('activitypreview.like')}</span>
                </Box>
              </Box>
            </Box>
            <Box component="span" className="reactions-react-button">
              <Box
                component="button"
                sx={{
                  padding: '4px 16px',
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: 'transparent',
                  border: 'none',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    id="comment-small"
                    width="16"
                    height="16"
                    fill="currentColor"
                  >
                    <path d="M5 8h5v1H5zm11-.5v.08a6 6 0 01-2.75 5L8 16v-3H5.5A5.51 5.51 0 010 7.5 5.62 5.62 0 015.74 2h4.76A5.5 5.5 0 0116 7.5zm-2 0A3.5 3.5 0 0010.5 4H5.74A3.62 3.62 0 002 7.5 3.53 3.53 0 005.5 11H10v1.33l2.17-1.39A4 4 0 0014 7.58zM5 7h6V6H5z"></path>
                  </svg>
                  <span>{t('activitypreview.comment')}</span>
                </Box>
              </Box>
            </Box>
            <Box component="span" className="reactions-react-button">
              <Box
                component="button"
                sx={{
                  padding: '4px 16px',
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: 'transparent',
                  border: 'none',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    id="repost-small"
                    width="16"
                    height="16"
                    fill="currentColor"
                  >
                    <path d="M4 10H2V5c0-1.66 1.34-3 3-3h3.85L7.42 0h2.44L12 3 9.86 6H7.42l1.43-2H5c-.55 0-1 .45-1 1v5zm8-4v5c0 .55-.45 1-1 1H7.15l1.43-2H6.14L4 13l2.14 3h2.44l-1.43-2H11c1.66 0 3-1.34 3-3V6h-2z"></path>
                  </svg>
                  <span>{t('activitypreview.repost')}</span>
                </Box>
              </Box>
            </Box>
            <Box component="span" className="reactions-react-button">
              <Box
                component="button"
                sx={{
                  padding: '4px 16px',
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: 'transparent',
                  border: 'none',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    id="send-privately-small"
                    width="16"
                    height="16"
                    data-supported-dps="16x16"
                    fill="currentColor"
                  >
                    <path d="M14 2L0 6.67l5 2.64 5.67-3.98L6.7 11l2.63 5L14 2z"></path>
                  </svg>
                  <span>{t('activitypreview.send')}</span>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        {previewComments.length > 0 && (
          <Box
            className="feed-shared-update-v2__comments-container"
            sx={{display: 'flex', flexDirection: 'column', margin: 0, padding: 0, border: 'none'}}
          >
            <Box
              className="comments-comment-box"
              sx={{
                padding: '4px 16px 8px',
                flexWrap: 'wrap',
                display: 'flex',
                alignItems: 'flex-start',
                borderTop: '1px solid transparent',
              }}
            >
              <Box
                className="comments-comment-box__form-container"
                sx={{maxWidth: '100%', minWidth: 0, border: 'solid transparent', borderWidth: '3px 0', flexGrow: 1}}
              >
                <Box
                  className="comments-comment-box__form"
                  sx={{display: 'block', boxSizing: 'border-box', minWidth: 0, padding: 0, margin: 0}}
                >
                  <Box
                    className="comments-comment-texteditor"
                    sx={{border: '1px solid rgb(0 0 0/.3)', borderRadius: '20px'}}
                  >
                    <Box sx={{display: 'flex', flexWrap: 'wrap'}}>
                      <Box
                        className="comments-comment-box-comment__text-editor"
                        sx={{
                          fontSize: '14px',
                          flex: '1 1 auto',
                          padding: '9.5px',
                          lineHeight: '21px',
                          color: 'rgba(0 0 0/.6)',
                        }}
                      >
                        <span>{t('activitypreview.addComment')}</span>
                      </Box>
                      <Box sx={{display: 'flex', marginLeft: 'auto'}}>
                        <Box sx={{width: '40px', height: '40px', display: 'flex', alignItems: 'center'}}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            id="emoji-medium"
                            width="24"
                            height="24"
                            fill="currentColor"
                          >
                            <path d="M8 10.5A1.5 1.5 0 119.5 12 1.5 1.5 0 018 10.5zm6.5 1.5a1.5 1.5 0 10-1.5-1.5 1.5 1.5 0 001.5 1.5zm7.5 0A10 10 0 1112 2a10 10 0 0110 10zm-2 0a8 8 0 10-8 8 8 8 0 008-8zm-8 4a6 6 0 01-4.24-1.76l-.71.76a7 7 0 009.89 0l-.71-.71A6 6 0 0112 16z"></path>
                          </svg>
                        </Box>
                        <Box sx={{width: '40px', height: '40px', display: 'flex', alignItems: 'center'}}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            id="image-medium"
                            width="24"
                            height="24"
                            fill="currentColor"
                          >
                            <path d="M19 4H5a3 3 0 00-3 3v10a3 3 0 003 3h14a3 3 0 003-3V7a3 3 0 00-3-3zm1 13a1 1 0 01-.29.71L16 14l-2 2-6-6-4 4V7a1 1 0 011-1h14a1 1 0 011 1zm-2-7a2 2 0 11-2-2 2 2 0 012 2z"></path>
                          </svg>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box className="comments-comments-list" sx={{padding: 0, borderRadius: '0 0 8px 8px', maxWidth: '100%'}}>
              {previewComments.map((comment) => (
                <PreviewCommentItem {...comment} key={comment.key} />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  },
);
